/**
 * CR-3: Outline Review Page
 *
 * Displays the generated outline (headings, sections, word targets) for user
 * review before proceeding with full content generation. Users can:
 * - Reorder sections (drag-and-drop)
 * - Edit headings inline
 * - Adjust per-section word targets
 * - Add/remove sections
 * - Approve to continue or reject to cancel
 */

import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Check,
  GripVertical,
  Pencil,
  Plus,
  Trash2,
  FileText,
  Loader2,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { blogCreationService } from '@/services/blogCreationService';
import type { OutlineSection } from '@/components/BlogCreationCommandCenter/types';

export default function OutlineReviewPage() {
  const { contentId } = useParams<{ contentId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch outline data
  const {
    data: outline,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['outline-review', contentId],
    queryFn: () => blogCreationService.getOutline(contentId!),
    enabled: !!contentId,
  });

  // Local editable state
  const [editedTitle, setEditedTitle] = useState<string | null>(null);
  const [editedSections, setEditedSections] = useState<OutlineSection[] | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const sections = editedSections ?? outline?.sections ?? [];
  const title = editedTitle ?? outline?.title ?? '';
  const hasEdits = editedTitle !== null || editedSections !== null;

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: () =>
      blogCreationService.approveOutline(contentId!, {
        title: hasEdits ? title : undefined,
        sections: hasEdits ? sections : undefined,
      }),
    onSuccess: () => {
      toast.success('Outline approved! Content generation resuming...');
      queryClient.invalidateQueries({ queryKey: ['content-list'] });
      navigate('/content/planner');
    },
    onError: (err: Error) => {
      toast.error(`Failed to approve outline: ${err.message}`);
    },
  });

  // Section editing handlers
  const updateSection = useCallback(
    (index: number, updates: Partial<OutlineSection>) => {
      const current = editedSections ?? outline?.sections ?? [];
      const updated = [...current];
      updated[index] = { ...updated[index], ...updates };
      setEditedSections(updated);
    },
    [editedSections, outline],
  );

  const removeSection = useCallback(
    (index: number) => {
      const current = editedSections ?? outline?.sections ?? [];
      setEditedSections(current.filter((_, i) => i !== index));
    },
    [editedSections, outline],
  );

  const addSection = useCallback(() => {
    const current = editedSections ?? outline?.sections ?? [];
    const newSection: OutlineSection = {
      section_id: `section_new_${Date.now()}`,
      heading: 'New Section',
      heading_level: 2,
      target_words: 200,
      key_points: [],
    };
    setEditedSections([...current, newSection]);
    setEditingIndex(current.length);
  }, [editedSections, outline]);

  const moveSection = useCallback(
    (fromIndex: number, direction: 'up' | 'down') => {
      const current = editedSections ?? outline?.sections ?? [];
      const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
      if (toIndex < 0 || toIndex >= current.length) return;
      const updated = [...current];
      [updated[fromIndex], updated[toIndex]] = [updated[toIndex], updated[fromIndex]];
      setEditedSections(updated);
    },
    [editedSections, outline],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-6 h-6 animate-spin text-coral" />
        <span className="ml-2 text-muted-foreground">Loading outline...</span>
      </div>
    );
  }

  if (error || !outline) {
    return (
      <div className="p-6">
        <div className="text-destructive">
          {error instanceof Error ? error.message : 'Failed to load outline'}
        </div>
        <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }

  const totalWords = sections.reduce((sum, s) => sum + s.target_words, 0);

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold">Review Outline</h1>
            <p className="text-sm text-muted-foreground">
              Edit headings, reorder sections, and adjust word targets before generating
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-sm">
            {sections.length} sections
          </Badge>
          <Badge variant="outline" className="text-sm">
            ~{totalWords} words
          </Badge>
        </div>
      </div>

      {/* Title */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-muted-foreground">Article Title</CardTitle>
        </CardHeader>
        <CardContent>
          <Input
            value={title}
            onChange={(e) => setEditedTitle(e.target.value)}
            className="text-lg font-semibold"
            placeholder="Article title..."
          />
          <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
            <FileText className="w-4 h-4" />
            <span>Keyword: <strong>{outline.keyword}</strong></span>
            <span className="mx-2">|</span>
            <span>Target: <strong>{outline.target_word_count} words</strong></span>
          </div>
        </CardContent>
      </Card>

      {/* Strategy Summary */}
      {outline.strategy_summary && (
        <Card className="bg-teal-light border-teal/20">
          <CardContent className="pt-4">
            <p className="text-sm font-medium text-teal-dark mb-1">Strategy Summary</p>
            <p className="text-sm">{outline.strategy_summary}</p>
          </CardContent>
        </Card>
      )}

      {/* Sections */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Sections</h2>
          <Button variant="outline" size="sm" onClick={addSection}>
            <Plus className="w-4 h-4 mr-1" />
            Add Section
          </Button>
        </div>

        {sections.map((section, index) => (
          <Card
            key={section.section_id}
            className="group hover:border-coral/30 transition-colors"
          >
            <CardContent className="py-3 px-4">
              <div className="flex items-center gap-3">
                {/* Drag handle + reorder buttons */}
                <div className="flex flex-col gap-0.5">
                  <GripVertical className="w-4 h-4 text-muted-foreground/40" />
                  <div className="flex flex-col">
                    <button
                      className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-30"
                      onClick={() => moveSection(index, 'up')}
                      disabled={index === 0}
                    >
                      &#9650;
                    </button>
                    <button
                      className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-30"
                      onClick={() => moveSection(index, 'down')}
                      disabled={index === sections.length - 1}
                    >
                      &#9660;
                    </button>
                  </div>
                </div>

                {/* Heading level badge */}
                <Badge
                  variant="secondary"
                  className="text-xs shrink-0 cursor-pointer"
                  onClick={() => {
                    const nextLevel = section.heading_level === 2 ? 3 : 2;
                    updateSection(index, { heading_level: nextLevel });
                  }}
                >
                  H{section.heading_level}
                </Badge>

                {/* Heading text */}
                {editingIndex === index ? (
                  <Input
                    autoFocus
                    value={section.heading}
                    onChange={(e) => updateSection(index, { heading: e.target.value })}
                    onBlur={() => setEditingIndex(null)}
                    onKeyDown={(e) => e.key === 'Enter' && setEditingIndex(null)}
                    className="flex-1"
                  />
                ) : (
                  <span
                    className={`flex-1 cursor-pointer hover:text-coral ${
                      section.heading_level === 2 ? 'font-semibold' : 'font-normal text-muted-foreground'
                    }`}
                    onClick={() => setEditingIndex(index)}
                  >
                    {section.heading || 'Untitled section'}
                  </span>
                )}

                {/* Word target */}
                <div className="flex items-center gap-1 shrink-0">
                  <Input
                    type="number"
                    value={section.target_words}
                    onChange={(e) =>
                      updateSection(index, { target_words: parseInt(e.target.value) || 0 })
                    }
                    className="w-20 h-8 text-sm text-right"
                    min={0}
                    max={2000}
                  />
                  <span className="text-xs text-muted-foreground">words</span>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setEditingIndex(editingIndex === index ? null : index)}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive"
                    onClick={() => removeSection(index)}
                    disabled={sections.length <= 1}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>

              {/* Key points (if any) */}
              {section.key_points.length > 0 && (
                <div className="mt-2 ml-12 space-y-1">
                  {section.key_points.map((point, i) => (
                    <p key={i} className="text-sm text-muted-foreground">
                      - {point}
                    </p>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Footer actions */}
      <div className="flex items-center justify-between pt-4 border-t">
        <div className="text-sm text-muted-foreground">
          {hasEdits && (
            <span className="text-coral">You have unsaved edits</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate('/content/planner')}>
            Cancel
          </Button>
          <Button
            onClick={() => approveMutation.mutate()}
            disabled={approveMutation.isPending || sections.length === 0}
            className="bg-coral hover:bg-coral-hover text-white"
          >
            {approveMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Approving...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Approve & Generate
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
